import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SellIcon from '@mui/icons-material/Sell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useQuery } from '@tanstack/react-query';
import { getBranches } from 'api/branchApi';
import { listClients } from 'api/clientsApi';
import BusinessIcon from '@mui/icons-material/Business';

export type ImportRateScope = 'default' | 'buy' | 'sell';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 260,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
        ...theme.applyStyles('dark', {
          color: 'inherit',
        }),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));

// Shared by Consumable and Equipment's "which template" import buttoon
export default function ImportRateCardMenuButton({
  onSelect,
  idPrefix,
}: {
  onSelect: (scope: ImportRateScope) => void;
  idPrefix: string;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { data: branches } = useQuery(['branches'], getBranches);
  const { data: clients } = useQuery(['list-clients'], listClients);
  const hasBranches = (branches?.length ?? 0) > 0;
  const hasClients = (clients?.length ?? 0) > 0;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const choose = (scope: ImportRateScope) => {
    handleClose();
    onSelect(scope);
  };

  const buttonId = `${idPrefix}-button`;
  const menuId = `${idPrefix}-menu`;

  return (
    <div>
      <Button
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        color="primary"
        size="large"
        disableElevation
        sx={{ px: '2rem' }}
        onClick={handleClick}
        startIcon={<CloudUploadIcon />}
        endIcon={<KeyboardArrowDownIcon />}>
        Import
      </Button>
      <StyledMenu id={menuId} anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => choose('default')} disableRipple>
          <BusinessIcon />
          Default Buy &amp; Sell Rates
        </MenuItem>
        {hasBranches && (
          <MenuItem onClick={() => choose('buy')} disableRipple>
            <ShoppingBagIcon />
            Branch Specific Buy Rates
          </MenuItem>
        )}
        {(hasBranches || hasClients) && (
          <MenuItem onClick={() => choose('sell')} disableRipple>
            <SellIcon />
            Branch/Client Specific Sell Rates
          </MenuItem>
        )}
      </StyledMenu>
    </div>
  );
}
